import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { LAYOUT_MODAL_ANIMATION_DURATION } from 'admin-panel/constants/ec-timeouts';
import { Task, task, timeout } from 'ember-concurrency';
import LayoutModalIconWrapperComponent from './modal/icon-wrapper';

import type ModalManagerService from 'admin-panel/services/modal-manager';

export interface LayoutModalContext {
  exitAnimationTask: Task<void, []>;
}

type WithOnCloseOrCleanup = {
  containerClass?: string;
  variant?: 'form';
  onSubmit?: (context: LayoutModalContext) => void;
  onInsert?: (context: LayoutModalContext) => void;
} & (
  | {
      onClose?: never;
      onCleanup?: () => void;
    }
  | {
      onCleanup?: never;
      onClose?: (context: LayoutModalContext) => void;
    }
);

interface LayoutModalComponentArgs {
  Args: {
    Named: WithOnCloseOrCleanup;
  };
  Blocks: {
    icon: [typeof LayoutModalIconWrapperComponent];
    title: [];
    content: [];
    controls: [{ onClose: () => void }];
  };
}

export default class LayoutModalComponent extends Component<LayoutModalComponentArgs> {
  @service
  declare modalManager: ModalManagerService;

  @tracked
  show = true;

  context: LayoutModalContext;

  constructor(owner: unknown, args: LayoutModalComponentArgs['Args']['Named']) {
    super(owner, args);

    this.context = {
      exitAnimationTask: this.exitAnimationTask,
    };

    this.modalManager.register(this.context);
  }

  willDestroy() {
    super.willDestroy();

    this.modalManager.unregister(this.context);
  }

  exitAnimationTask = task({ drop: true }, async () => {
    this.show = false;

    await timeout(LAYOUT_MODAL_ANIMATION_DURATION);

    this.args.onCleanup?.();
  });

  @action
  onClose() {
    if (this.args.onClose) {
      this.args.onClose(this.context);
    } else {
      this.exitAnimationTask.perform();
    }
  }

  @action
  onInsert() {
    this.args.onInsert?.(this.context);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Layout::Modal': typeof LayoutModalComponent;
    'layout/modal': typeof LayoutModalComponent;
  }
}
